import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import websocketService from '../../utils/websocketService';

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInHours / 24;

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
  if (diffInDays < 7) return `${Math.floor(diffInDays)}d ago`;
  return date.toLocaleDateString();
};

const NotificationsPanel = ({ user, isOpen, onClose }) => {
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingActions, setPendingActions] = useState(new Set());
  const VALID_TABS = ['all', 'updates', 'messages', 'invitations'];
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem('notifications-tab');
      return VALID_TABS.includes(saved) ? saved : 'all';
    } catch {
      return 'all';
    }
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    try {
      localStorage.setItem('notifications-tab', tab);
    } catch (e) {
      // ignore
    }
  };

  const fetchInvitations = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/project-invitations/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching project invitations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async (invitationId) => {
    if (pendingActions.has(invitationId)) return;
    try {
      setPendingActions(prev => new Set([...prev, invitationId]));
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/project-invitations/${invitationId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      window.dispatchEvent(new CustomEvent('projectsUpdated'));
      window.dispatchEvent(new CustomEvent('notificationsUpdated'));
    } catch (error) {
      console.error('Error accepting invitation:', error);
    } finally {
      setPendingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  const handleDecline = async (invitationId) => {
    if (pendingActions.has(invitationId)) return;
    try {
      setPendingActions(prev => new Set([...prev, invitationId]));
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/project-invitations/${invitationId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      window.dispatchEvent(new CustomEvent('notificationsUpdated'));
    } catch (error) {
      console.error('Error declining invitation:', error);
    } finally {
      setPendingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitationId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    const handleWebSocketMessage = (data) => {
      if (data.type === 'project_invitation_received') {
        setInvitations(prev => [data.invitation, ...prev]);
      }
    };
    const token = localStorage.getItem('token');
    if (token && user?.isOnboarded) {
      websocketService.connect(token);
      websocketService.addListener('notifications-panel', handleWebSocketMessage);
      return () => websocketService.removeListener('notifications-panel');
    }
  }, [user?.id, user?.isOnboarded]);

  useEffect(() => {
    if (isOpen && user?.id) fetchInvitations();
  }, [isOpen, user?.id]);

  if (!isOpen) return null;

  // Group invitations by date for the timeline layout
  const groupInvitationsByDate = () => {
    const groups = {};
    invitations.forEach((inv) => {
      const date = new Date(inv.createdAt);
      const key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(inv);
    });
    return Object.entries(groups).sort((a, b) => new Date(b[1][0].createdAt) - new Date(a[1][0].createdAt));
  };

  const filteredInvitations = ['all', 'messages', 'invitations'].includes(activeTab) ? invitations : [];
  const invitationGroups = groupInvitationsByDate();

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white dark:bg-transparent">
      {/* Title */}
      <div className="flex items-center justify-center px-6 pt-2 pb-2">
        <h2 className="text-xl font-semibold text-black dark:text-white">
          Notifications
        </h2>
      </div>
      {/* Tabs - segmented control matching design: dark container, pill for selected */}
      <div className="flex justify-center px-6 py-4">
        <div className="inline-flex p-0.5 rounded-lg bg-gray-200 dark:bg-[#1a1a1a] border border-gray-300 dark:border-[#2a2a2a]">
          {['All', 'Updates', 'Messages', 'Invitations'].map((tab) => {
            const isActive = activeTab === tab.toLowerCase();
            return (
              <button
                key={tab}
                onClick={() => handleTabChange(tab.toLowerCase())}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-white dark:bg-[#252525] text-black dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-[#A0A0A0] hover:text-gray-900 dark:hover:text-gray-300'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content - timeline with date sections */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-black dark:border-t-white" />
          </div>
        ) : filteredInvitations.length === 0 ? (
          <div className="p-6 text-center py-16 text-[#737373] dark:text-[#999999]">
            <p className="text-sm">All caught up</p>
          </div>
        ) : (
          <div className="flex gap-8 p-6">
            {/* Left: date markers */}
            <div className="flex-shrink-0 w-40 pt-1 space-y-8">
              {invitationGroups.map(([dateKey]) => (
                <div key={dateKey} className="text-xs font-medium text-gray-500 dark:text-gray-500">
                  Updates / {dateKey}
                </div>
              ))}
            </div>
            {/* Right: notification cards */}
            <div className="flex-1 min-w-0 space-y-6">
              {invitationGroups.map(([dateKey, groupInvs]) => (
                <div key={dateKey} className="space-y-4">
                  {groupInvs.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="p-5 rounded-xl border border-gray-200 dark:border-[#1a1a1a] bg-gray-50/50 dark:bg-[#0d0d0d]"
                    >
                      <div className="flex items-start gap-3 mb-4">
                        {invitation.invitedBy?.profileImage?.url ? (
                          <img
                            src={invitation.invitedBy.profileImage.url}
                            alt={invitation.invitedBy.name}
                            className="w-10 h-10 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <UserCircleIcon className="w-10 h-10 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 dark:text-white">
                            <span className="font-medium">{invitation.invitedBy?.name}</span> invited you to join
                          </p>
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white truncate mt-0.5">
                            {invitation.project?.name}
                          </h4>
                          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {formatTimeAgo(invitation.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAccept(invitation.id)}
                          disabled={pendingActions.has(invitation.id)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2.5 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          {pendingActions.has(invitation.id) ? (
                            <div className="w-4 h-4 border-2 border-white dark:border-black border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <CheckIcon className="w-4 h-4 mr-2" />
                              Accept
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDecline(invitation.id)}
                          disabled={pendingActions.has(invitation.id)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2.5 bg-gray-200 dark:bg-[#1a1a1a] hover:bg-gray-300 dark:hover:bg-[#222] text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          <XMarkIcon className="w-4 h-4 mr-2" />
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
